// React Components
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

// Material UI Components
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

// Audit Vault Components
import ServiceIsDown from "../components/common/ServiceIsDown";
import TenantStats from "../components/TenantStats";
import CompanyDetails from "../components/company/CompanyDetails";
import ProgressBar from "../components/common/ProgressBar";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import BillingInfo from "../components/billing/BillingInfo";

import signInWithMsLogo from '../imgs/buttonSignInWithMicrosoft.svg'
import { loginToMicrosoft } from '../utilities/common-user-utils';
import { useMsal } from '@azure/msal-react';

// Audit Vault Common
import { getCompanyByCompanyId } from "../utilities/common-company";

// Audit Vault Constants
import { LINK_TO_SAAS_CUSTOMER_AGREEMENT, LINK_TO_PRIVACY_POLICY } from '../constants/constants-links';

const theSectionTitle = "Company";
const theSectionDesc = "Welcome to your Company Dashboard. This is where you can review and configure your Company's respective settings.";
const isAdminPage = false;

const signInTitle = "Enhanced Compliance Confidence";
const signInDescription = "Preserve, search, and report on Microsoft 365 audit history for as long as your organization requires.";

const landingBenefits = [
  {
    title: "Preserve audit history",
    description: "Retain valuable Microsoft 365 audit records beyond the standard availability window.",
    icon: ArchiveOutlinedIcon,
  },
  {
    title: "Search and report",
    description: "Surface activity across SharePoint, Exchange, Entra ID, and other supported workloads.",
    icon: SearchOutlinedIcon,
  },
  {
    title: "Strengthen compliance",
    description: "Support investigations, governance, and regulated-record requirements with dependable history.",
    icon: VerifiedUserOutlinedIcon,
  },
];

const theBreadcrumbPath = [
  { name: 'Home', link: '/' },
  { name: 'Company', link: '/MyCompany' },
];

function MyCompany(props) {

  const { user, openCompanyNag, setOpenCompanyNag } = props;

  // Component Constants
  const [company, setCompany] = useState("");
  const [subscription, setSubscription] = useState("");
  const [loading, setLoading] = useState(true);
  const [serviceIsDownError, setServiceIsDownError] = useState(false);
  const [showCompanySetupNow, setShowCompanySetupNow] = useState(false);

  const navigate = useNavigate();
  const { instance } = useMsal();


  // Component functions
  const handleUpdate = (value) => {
    if (value > 0) {
      navigate(`/MyCompany/company-update/${value}`);
    }
  };

  // When the sign up button is clicked.
  const handleSignUpClick = () => {
    setShowCompanySetupNow(true);
  };


  useEffect(() => {

    async function fetchData() {
      try {

        if (loading) {
          if (user) {
            const response = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
            if (response) {
              setCompany(response);
            }
          }
        }
      }
      catch (e) {
        console.log("ERROR: MyCompany.fetchData");
        console.log(e);
        setServiceIsDownError(true);
      }
      finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [loading, user]);


  // Component UI
  if (serviceIsDownError) {
    return (<><ServiceIsDown></ServiceIsDown></>);
  }

  if (loading) {
    return (
      <><div><ProgressBar loading={loading} message="Loading ..." /></div></>
    );
  }

  if (!loading && !company) {
    if (showCompanySetupNow) {
      return <Navigate to="/mycompanysetupcreate" replace />;
    }

    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2 }, py: { xs: 2, md: 5 } }}>
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 3, md: 4 },
            border: '1px solid',
            borderColor: 'primary.light',
            background: 'linear-gradient(135deg, #eaf6ff 0%, #f7fbff 52%, #e8f1ff 100%)',
          }}
        >
          <Grid container alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Box sx={{ p: { xs: 3, sm: 5, md: 7 }, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 4, md: 7 } }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/favicon.ico`}
                    alt="Audit Vault"
                    width="43"
                    height="43"
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                    Audit Vault for Microsoft 365
                  </Typography>
                </Box>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                    lineHeight: 1.08,
                    fontWeight: 750,
                    letterSpacing: '-0.035em',
                    color: 'text.primary',
                    maxWidth: 680,
                  }}
                >
                  {signInTitle}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mt: 2.5, maxWidth: 650, lineHeight: 1.55, fontWeight: 400 }}
                >
                  {signInDescription}
                </Typography>
                <Typography variant="body2" sx={{ mt: 4, color: 'primary.dark', fontWeight: 600 }}>
                  Built for Microsoft 365 &bull; Microsoft identity &bull; No separate password
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: 'flex', p: { xs: 2, sm: 4, md: 5 } }}>
              <Card
                elevation={0}
                sx={{
                  width: '100%',
                  alignSelf: 'center',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 18px 45px rgba(35, 73, 120, 0.12)',
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                    Get started
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                    Sign in securely with your Microsoft work account.
                  </Typography>
                  <Button
                    fullWidth
                    onClick={() => loginToMicrosoft(instance)}
                    sx={{ p: 0.5, minHeight: 44, '&:hover': { backgroundColor: 'transparent' } }}
                  >
                    <img src={signInWithMsLogo} alt="Sign in with Microsoft" style={{ maxWidth: '100%' }} />
                  </Button>
                  <Button fullWidth variant="contained" onClick={handleSignUpClick} sx={{ mt: 1.5, minHeight: 44 }}>
                    Create an account
                  </Button>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 3, textAlign: 'center' }}>
                    Existing customers sign in above. New customers can create an account to begin setup.
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 2, textAlign: 'center' }}>
                    <a href={LINK_TO_SAAS_CUSTOMER_AGREEMENT} target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    &nbsp; | &nbsp;
                    <a href={LINK_TO_PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2.5} sx={{ mt: { xs: 1, md: 2 } }}>
          {landingBenefits.map(({ title, description, icon: BenefitIcon }) => (
            <Grid item xs={12} md={4} key={title}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', backgroundColor: 'primary.50', color: 'primary.main', mb: 2 }}>
                    <BenefitIcon />
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.65 }}>{description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!loading && company) {
    return (
      <>
        <CustomerPageScaffold breadcrumbPaths={theBreadcrumbPath} sectionTitle={theSectionTitle} sectionDescription={theSectionDesc} isAdminPage={isAdminPage} company={company} openCompanyNag={openCompanyNag} setOpenCompanyNag={setOpenCompanyNag} />
        {!loading && company && company.setupStageComplete > 0 &&
          <div>
            <Grid container spacing={2}>
              {
                company.tenantList.map((tenant) =>
                  <Grid
                    key={tenant.id}
                    item
                    xs={12}
                    md={6}
                    sx={{ p: 2 }}
                  >
                    <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray' }}>
                      <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                        Tenant Summary
                      </Typography>
                      <TenantStats tenant={tenant} user={user} reportPageId={9} />
                    </Paper>
                  </Grid>
                )
              }
              {
                company.stripeCustomerId && subscription && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ p: 2 }}
                  >
                    <Paper key="billing" elevation={3} sx={{ p: 2, border: '1px solid lightgray' }}>
                      <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                        Billing and Payments
                      </Typography>
                      <BillingInfo subscription={subscription} companyId={company.id} />
                      <br />
                    </Paper>
                  </Grid>)
              }
              <Grid
                item
                xs={12}
                sx={{ p: 2 }}
              >
                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray' }}>
                  <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 1 }}>
                    Your Company Details
                  </Typography>
                  <CompanyDetails company={company} />
                  <br />
                  <div>
                    <Button
                      size='small'
                      variant="contained"
                      type="button"
                      onClick={(event) => handleUpdate(company.id)}
                    >
                      Edit
                    </Button>
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </div>
        }
      </>

    );
  }
}

export default MyCompany;
